<template>
  <div class="h-100">
    <Layout ref="layout">
      <template v-slot:body>
        <div class="container-fluid p-4">
          <div class="d-flex justify-content-between align-items-center mb-4">
            <div>
              <h4 class="mb-1">Detalhes da Leitura</h4>
              <p class="text-muted mb-0" v-if="leitura">
                {{ formatDateTime(leitura.dateTime) }}
              </p>
            </div>
            <button class="btn btn-secondary" @click="voltar">
              <i class="bx bx-arrow-back"></i> Voltar
            </button>
          </div>

          <div v-if="loading" class="text-center py-5">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">Carregando...</span>
            </div>
          </div>

          <div v-else-if="leitura">
            <!-- Card de Metadata -->
            <div class="card mb-4" :class="{ 'border-danger': leitura.isDeleted }">
              <div class="card-header d-flex justify-content-between align-items-center">
                <h5 class="mb-0">Metadados da Leitura</h5>
                <div>
                  <span v-if="leitura.isDeleted" class="badge bg-danger me-2">
                    Deletado em {{ formatDateTime(leitura.deletedAt) }}
                  </span>
                  <button
                    v-if="!leitura.isDeleted"
                    class="btn btn-sm btn-outline-danger"
                    @click="confirmarDeleteMetadata"
                  >
                    <i class="bx bx-trash"></i> Deletar Leitura
                  </button>
                  <button
                    v-else
                    class="btn btn-sm btn-outline-success"
                    @click="confirmarRestoreMetadata"
                  >
                    <i class="bx bx-undo"></i> Restaurar Leitura
                  </button>
                </div>
              </div>
              <div class="card-body">
                <div class="row">
                  <div class="col-md-3 mb-3">
                    <label class="text-muted small">ID</label>
                    <p class="mb-0">{{ leitura.id }}</p>
                  </div>
                  <div class="col-md-3 mb-3">
                    <label class="text-muted small">QR Code ID</label>
                    <p class="mb-0">{{ leitura.qrCodeId }}</p>
                  </div>
                  <div class="col-md-3 mb-3">
                    <label class="text-muted small">Data/Hora</label>
                    <p class="mb-0">{{ formatDateTime(leitura.dateTime) }}</p>
                  </div>
                  <div class="col-md-3 mb-3">
                    <label class="text-muted small">Origem</label>
                    <p class="mb-0">{{ leitura.infoOrigem || '-' }}</p>
                  </div>
                  <div class="col-md-3 mb-3">
                    <label class="text-muted small">Versão Firmware</label>
                    <p class="mb-0">{{ leitura.infoFirmwareVersion || '-' }}</p>
                  </div>
                  <div class="col-md-3 mb-3">
                    <label class="text-muted small">Versão OnConnect</label>
                    <p class="mb-0">{{ leitura.infoVersaoOnconnect || '-' }}</p>
                  </div>
                  <div class="col-md-3 mb-3">
                    <label class="text-muted small">WiFi RSSI</label>
                    <p class="mb-0">{{ leitura.infoWifiCliRssi || '-' }}</p>
                  </div>
                  <div class="col-md-3 mb-3">
                    <label class="text-muted small">Bateria OnConnect</label>
                    <p class="mb-0">{{ leitura.infoBateriaOnconnect || '-' }}</p>
                  </div>
                </div>
                <div v-if="leitura.deletedReason" class="alert alert-warning mt-3 mb-0">
                  <strong>Motivo da exclusão:</strong> {{ leitura.deletedReason }}
                </div>
              </div>
            </div>

            <!-- Cards de Sensores -->
            <h5 class="mb-3">Sensores ({{ leitura.sensores.length }})</h5>

            <div v-if="leitura.sensores.length === 0" class="text-center py-4">
              <p class="text-muted">Nenhum sensor registrado nesta leitura</p>
            </div>

            <div v-else class="row">
              <div
                v-for="sensor in leitura.sensores"
                :key="`${sensor.tipoSensor}-${sensor.id}`"
                class="col-md-6 mb-4"
              >
                <div class="card h-100" :class="{ 'border-danger': sensor.isDeleted }">
                  <div class="card-header d-flex justify-content-between align-items-center">
                    <div>
                      <span class="badge bg-primary me-2">
                        {{ formatSensorType(sensor.tipoSensor) }}
                      </span>
                      <span v-if="sensor.isDeleted" class="badge bg-danger">
                        Deletado
                      </span>
                    </div>
                    <div>
                      <button
                        v-if="!sensor.isDeleted"
                        class="btn btn-sm btn-outline-danger"
                        @click="confirmarDeleteSensor(sensor)"
                        title="Deletar sensor"
                      >
                        <i class="bx bx-trash"></i>
                      </button>
                      <button
                        v-else
                        class="btn btn-sm btn-outline-success"
                        @click="confirmarRestoreSensor(sensor)"
                        title="Restaurar sensor"
                      >
                        <i class="bx bx-undo"></i>
                      </button>
                    </div>
                  </div>
                  <div class="card-body">
                    <div class="row mb-2">
                      <div class="col-6">
                        <small class="text-muted">Sensor ID:</small>
                        <p class="mb-0 small">{{ sensor.sensorId || '-' }}</p>
                      </div>
                      <div class="col-6">
                        <small class="text-muted">Source ID:</small>
                        <p class="mb-0 small">{{ sensor.sourceId || '-' }}</p>
                      </div>
                    </div>
                    <div class="row mb-2">
                      <div class="col-12">
                        <small class="text-muted">Modelo:</small>
                        <p class="mb-0 small">{{ sensor.modelo || '-' }}</p>
                      </div>
                    </div>
                    <hr />
                    <div class="row">
                      <div
                        v-for="(valor, chave) in sensor.dados"
                        :key="chave"
                        class="col-6 mb-2"
                      >
                        <small class="text-muted">{{ formatLabel(chave) }}:</small>
                        <p class="mb-0 small">{{ valor !== null ? valor : '-' }}</p>
                      </div>
                    </div>
                    <div v-if="sensor.deletedReason" class="alert alert-warning mt-3 mb-0 small">
                      <strong>Motivo:</strong> {{ sensor.deletedReason }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div v-else class="text-center py-5">
            <p class="text-muted">Leitura não encontrada</p>
          </div>
        </div>
      </template>
    </Layout>

    <!-- Modal de Confirmação de Delete Metadata -->
    <div class="modal fade" ref="modalDeleteMetadata" tabindex="-1">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Confirmar Exclusão</h5>
            <button type="button" class="btn-close" @click="fecharModalDeleteMetadata"></button>
          </div>
          <div class="modal-body">
            <p>Tem certeza que deseja deletar esta leitura inteira?</p>
            <p class="text-muted small">
              Esta ação marcará a leitura e todos os seus sensores como deletados.
            </p>
            <div class="mb-3">
              <label class="form-label">Motivo (opcional)</label>
              <textarea
                class="form-control"
                v-model="deleteReason"
                rows="2"
                placeholder="Informe o motivo da exclusão..."
              ></textarea>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="fecharModalDeleteMetadata">
              Cancelar
            </button>
            <button type="button" class="btn btn-danger" @click="executarDeleteMetadata" :disabled="processando">
              <span v-if="processando" class="spinner-border spinner-border-sm me-1"></span>
              Deletar
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal de Confirmação de Delete Sensor -->
    <div class="modal fade" ref="modalDeleteSensor" tabindex="-1">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Confirmar Exclusão do Sensor</h5>
            <button type="button" class="btn-close" @click="fecharModalDeleteSensor"></button>
          </div>
          <div class="modal-body">
            <p>
              Tem certeza que deseja deletar este sensor
              <strong>{{ sensorSelecionado ? formatSensorType(sensorSelecionado.tipoSensor) : '' }}</strong>?
            </p>
            <div class="mb-3">
              <label class="form-label">Motivo (opcional)</label>
              <textarea
                class="form-control"
                v-model="deleteReason"
                rows="2"
                placeholder="Informe o motivo da exclusão..."
              ></textarea>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="fecharModalDeleteSensor">
              Cancelar
            </button>
            <button type="button" class="btn btn-danger" @click="executarDeleteSensor" :disabled="processando">
              <span v-if="processando" class="spinner-border spinner-border-sm me-1"></span>
              Deletar
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal de Confirmação de Restore -->
    <div class="modal fade" ref="modalRestore" tabindex="-1">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Confirmar Restauração</h5>
            <button type="button" class="btn-close" @click="fecharModalRestore"></button>
          </div>
          <div class="modal-body">
            <p>{{ restoreMessage }}</p>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="fecharModalRestore">
              Cancelar
            </button>
            <button type="button" class="btn btn-success" @click="executarRestore" :disabled="processando">
              <span v-if="processando" class="spinner-border spinner-border-sm me-1"></span>
              Restaurar
            </button>
          </div>
        </div>
      </div>
    </div>

    <Alert ref="alert"></Alert>
  </div>
</template>

<script>
import Layout from "../../components/layout/Tema.vue";
import Alert from "../../components/Alert.vue";

export default {
  components: {
    Layout,
    Alert,
  },
  data() {
    return {
      qrCodeId: null,
      metadataId: null,
      leitura: null,
      loading: false,
      processando: false,
      deleteReason: "",
      sensorSelecionado: null,
      restoreType: null, // 'metadata' ou 'sensor'
      restoreMessage: "",
    };
  },
  mounted() {
    this.qrCodeId = this.$route.params.qrCodeId;
    this.metadataId = this.$route.params.metadataId;
    this.carregarLeitura();
  },
  methods: {
    async carregarLeitura() {
      this.loading = true;
      try {
        const response = await this.urlGet(`/AdminLeituras/${this.metadataId}`);
        if (response.data) {
          this.leitura = response.data;
        }
      } catch (error) {
        console.error("Erro ao carregar leitura:", error);
        this.$refs.alert.show("Erro ao carregar leitura", "danger");
      } finally {
        this.loading = false;
      }
    },
    formatDateTime(dateStr) {
      if (!dateStr) return "-";
      const date = new Date(dateStr);
      return date.toLocaleString("pt-BR");
    },
    formatSensorType(tipo) {
      const tipos = {
        estacao: "Estação",
        chuva: "Chuva",
        folha: "Folha",
        solo: "Solo",
        energia: "Energia",
        energia_smtp: "Energia SMTP",
        sonda_hidrostatica: "Sonda Hidrostática",
        piranometro: "Piranômetro",
        sonda_laser: "Sonda Laser",
        sonda_nivel: "Sonda Nível",
        sonda_caixa: "Caixa d'Água",
        ambiente: "Ambiente",
        ritter: "Ritter",
        fotobio: "Fotobio",
        lut: "LUT",
      };
      return tipos[tipo] || tipo;
    },
    formatLabel(chave) {
      return chave
        .replace(/_/g, " ")
        .replace(/\b\w/g, (l) => l.toUpperCase());
    },
    confirmarDeleteMetadata() {
      this.deleteReason = "";
      $(this.$refs.modalDeleteMetadata).modal({ backdrop: "static", keyboard: false });
    },
    confirmarDeleteSensor(sensor) {
      this.sensorSelecionado = sensor;
      this.deleteReason = "";
      $(this.$refs.modalDeleteSensor).modal({ backdrop: "static", keyboard: false });
    },
    confirmarRestoreMetadata() {
      this.restoreType = "metadata";
      this.restoreMessage = "Tem certeza que deseja restaurar esta leitura e todos os seus sensores?";
      $(this.$refs.modalRestore).modal({ backdrop: "static", keyboard: false });
    },
    confirmarRestoreSensor(sensor) {
      this.sensorSelecionado = sensor;
      this.restoreType = "sensor";
      this.restoreMessage = `Tem certeza que deseja restaurar o sensor ${this.formatSensorType(sensor.tipoSensor)}?`;
      $(this.$refs.modalRestore).modal({ backdrop: "static", keyboard: false });
    },
    fecharModalDeleteMetadata() {
      $(this.$refs.modalDeleteMetadata).modal("hide");
    },
    fecharModalDeleteSensor() {
      $(this.$refs.modalDeleteSensor).modal("hide");
    },
    fecharModalRestore() {
      $(this.$refs.modalRestore).modal("hide");
    },
    async executarDeleteMetadata() {
      this.processando = true;
      try {
        await this.urlDelete(`/AdminLeituras/Metadata/${this.metadataId}`, {
          reason: this.deleteReason || null,
        });
        this.$refs.alert.show("Leitura deletada com sucesso", "success");
        $(this.$refs.modalDeleteMetadata).modal("hide");
        this.carregarLeitura();
      } catch (error) {
        console.error("Erro ao deletar leitura:", error);
        this.$refs.alert.show("Erro ao deletar leitura", "danger");
      } finally {
        this.processando = false;
      }
    },
    async executarDeleteSensor() {
      if (!this.sensorSelecionado) return;

      this.processando = true;
      try {
        await this.urlDelete(
          `/AdminLeituras/Sensor/${this.sensorSelecionado.tipoSensor}/${this.sensorSelecionado.id}`,
          { reason: this.deleteReason || null }
        );
        this.$refs.alert.show("Sensor deletado com sucesso", "success");
        $(this.$refs.modalDeleteSensor).modal("hide");
        this.carregarLeitura();
      } catch (error) {
        console.error("Erro ao deletar sensor:", error);
        this.$refs.alert.show("Erro ao deletar sensor", "danger");
      } finally {
        this.processando = false;
      }
    },
    async executarRestore() {
      this.processando = true;
      try {
        if (this.restoreType === "metadata") {
          await this.urlPost(`/AdminLeituras/Restore/Metadata/${this.metadataId}`);
          this.$refs.alert.show("Leitura restaurada com sucesso", "success");
        } else if (this.restoreType === "sensor" && this.sensorSelecionado) {
          await this.urlPost(
            `/AdminLeituras/Restore/Sensor/${this.sensorSelecionado.tipoSensor}/${this.sensorSelecionado.id}`
          );
          this.$refs.alert.show("Sensor restaurado com sucesso", "success");
        }
        $(this.$refs.modalRestore).modal("hide");
        this.carregarLeitura();
      } catch (error) {
        console.error("Erro ao restaurar:", error);
        this.$refs.alert.show("Erro ao restaurar", "danger");
      } finally {
        this.processando = false;
      }
    },
    voltar() {
      // Usa router.back() para manter os filtros que estavam na listagem
      this.$router.back();
    },
  },
};
</script>

<style scoped>
.border-danger {
  border-color: #dc3545 !important;
}
</style>
